import React from 'react'
import { t } from 'ttag'
import { useSelector } from 'react-redux'
import { Form } from 'components/shared/Form/Form'
import { Modal } from 'components/shared/Modal/Modal'
import { fetchDepartments } from 'api/departments'
import {
  NAME_FIELD,
  LAST_NAME_FIELD,
  ROLE_FIELD,
  ADDRESS_FIELD,
  PASSWORD_FIELD,
  DEPARTMENT_FIELD,
  INVITE_FIELD,
} from './model'
import cx from 'classnames'
import { Button } from 'components/shared/Button/Button'
import styles from './ManageUserModal.module.scss'
import * as R from 'ramda'
import { roles, ADMIN_ROLE } from 'constants/users'
import { useModal } from './hooks'
import { EyeIcon, EyeOffIcon } from 'components/shared/Icon/model'
import { Icon } from 'components/shared/Icon/Icon'
import { getUser } from 'ducks/auth'

export const ManageUserModal = ({
  user,
  onCancel,
  onClose,
  onConfirm,
  isOpen,
  handleDeleteUserOpen,
}) => {
  const { pk } = useSelector(getUser)

  const {
    state: { control, formState, showPasswordField, isPasswordVisible },
    handlers: {
      handleCancel,
      handleClose,
      watch,
      handleFormSubmit,
      setShowPasswordField,
      setIsPasswordVisible,
    },
  } = useModal({ user, isOpen }, { onCancel, onClose, onConfirm })

  const passwordValue = control?._formValues[PASSWORD_FIELD]?.trim()
  const isEditMode = !R.isNil(user?.id)
  const isEditingCurrentUser = user?.id === pk
  const showDeleteButton = isEditMode && !isEditingCurrentUser
  const isAdminSelected = ADMIN_ROLE == watch(ROLE_FIELD)?.value

  return (
    <Modal
      cancelLabel={t`Cancel`}
      confirmLabel={t`Save`}
      title={!isEditMode ? t`Add user` : t`Edit user`}
      className={styles.modal}
      {...{
        onCancel: handleCancel,
        onClose: handleClose,
        onConfirm: handleFormSubmit,
        isOpen,
      }}
    >
      <Modal.Header />
      <Modal.Content className={styles.content}>
        <Form>
          <div className={styles.row}>
            <Form.Field
              error={formState.errors[NAME_FIELD]?.message}
              key={NAME_FIELD}
              field={'input'}
              label={`${t`First name`} *`}
              name={NAME_FIELD}
              isRequired
              control={control}
              count={watch(NAME_FIELD)?.length}
            />
            <Form.Field
              error={formState.errors[LAST_NAME_FIELD]?.message}
              key={LAST_NAME_FIELD}
              field={'input'}
              label={t`Last name`}
              name={LAST_NAME_FIELD}
              isRequired
              control={control}
              count={watch(LAST_NAME_FIELD)?.length}
            />
          </div>
          <div className={styles.row}>
            <Form.Field
              error={
                formState.errors?.root?.apiError.message[ADDRESS_FIELD] ||
                formState.errors[ADDRESS_FIELD]?.message
              }
              key={ADDRESS_FIELD}
              field={'input'}
              type="email"
              label={`${t`Email`} *`}
              name={ADDRESS_FIELD}
              isRequired
              control={control}
              count={watch(ADDRESS_FIELD)?.length}
            />
            <Form.Field
              error={formState.errors[ROLE_FIELD]?.message}
              key={ROLE_FIELD}
              field={'combobox'}
              label={`${t`Role`} *`}
              name={ROLE_FIELD}
              isRequired
              control={control}
              options={Object.keys(roles).map(code => ({
                value: code,
                label: roles[code],
              }))}
            />
          </div>
          <div className={styles.row}>
            {showPasswordField ? (
              <div className={styles.passwordField}>
                <Form.Field
                  error={formState.errors[PASSWORD_FIELD]?.message}
                  key={PASSWORD_FIELD}
                  field={'input'}
                  label={user ? t`Password` : `${t`Password`} *`}
                  name={PASSWORD_FIELD}
                  isRequired
                  type={isPasswordVisible ? 'text' : 'password'}
                  control={control}
                  count={watch(PASSWORD_FIELD)?.length}
                />
                <Icon
                  className={styles.eyeIcon}
                  icon={isPasswordVisible ? EyeIcon : EyeOffIcon}
                  size={20}
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                />
                {[
                  {
                    text: t`Your password must contain at least 8 characters.`,
                    getFulfilled: value => value?.length >= 8,
                  },
                  {
                    text: t`Your password cannot be entirely numeric.`,
                    getFulfilled: value =>
                      value && /(?!^\d+$)^.+$/g.test(value),
                  },
                ].map(({ text, getFulfilled }, index) => (
                  <div
                    key={index}
                    className={cx(styles.hint, {
                      [styles.fulfilled]: getFulfilled(passwordValue),
                    })}
                  >
                    {text}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={styles.showPassword}
                onClick={() => setShowPasswordField(!showPasswordField)}
              >
                {t`set new password`}
              </div>
            )}
            <Form.Field
              error={formState.errors[DEPARTMENT_FIELD]?.message}
              key={DEPARTMENT_FIELD}
              field={'combobox'}
              label={t`Department access`}
              name={DEPARTMENT_FIELD}
              placeholder={''}
              control={control}
              className={cx(styles.field, {
                [styles.visible]: !isEditMode && !isAdminSelected,
              })}
              isDisabled={isEditMode || isAdminSelected}
              fetchOptions={[
                fetchDepartments,
                ({ name, id }) => ({
                  label: name,
                  value: id,
                }),
              ]}
              isMulti
              isSearchable
              closeMenuOnSelect={false}
            />
          </div>
          <br />
          {isEditMode ? null : (
            <Form.Field
              error={formState.errors[INVITE_FIELD]?.message}
              key={INVITE_FIELD}
              field={'checkbox'}
              label={t`Send invite to the user`}
              name={INVITE_FIELD}
              isRequired
              control={control}
            />
          )}
        </Form>
      </Modal.Content>
      <Modal.Footer
        className={cx({ [styles.footer]: showDeleteButton })}
        customControls={
          showDeleteButton ? (
            <Button
              className={styles.delete}
              color="red"
              variant="filled"
              label={t`Delete`}
              size="medium"
              onClick={handleDeleteUserOpen}
            />
          ) : (
            undefined
          )
        }
      />
    </Modal>
  )
}
